import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PlayIcon, PlusIcon, ThumbsUpIcon, SearchIcon, BellIcon } from 'lucide-react';
import { ExitIntentModal } from '../components/ExitIntentModal';
import { initMetaPixel, trackPageView } from '../utils/metaPixel';
import { trackPage } from '../utils/analytics';
const YOUTUBE_EMBED_URL = 'https://www.youtube.com/embed/eWV0q6FDL3c?start=1049&rel=0&modestbranding=1';
const CTA_URL = `https://api.whatsapp.com/send?phone=524426056175&text=${encodeURIComponent('Hola, me interesa acceder a la Academia de Bienes Raíces...')}`;
const MONTHLY_PLAN_URL = `https://api.whatsapp.com/send?phone=524426056175&text=${encodeURIComponent('Me interesa el Plan Mensual de $37 USD de la Academia Bienes Raíces')}`;
const ANNUAL_PLAN_URL = `https://api.whatsapp.com/send?phone=524426056175&text=${encodeURIComponent('Me interesa el Plan Anual de la Academia Bienes Raíces')}`;
const NAV_LINKS = ['Inicio', 'Masterclass', 'Mi lista'];
export function NetflixBR() {
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [exitModalShown, setExitModalShown] = useState(false);
  useEffect(() => {
    initMetaPixel();
    trackPageView();
    trackPage('/netflix-br');
  }, []);

  // Exit intent — show the offer once when the cursor leaves toward the top
  useEffect(() => {
    if (exitModalShown) return;
    const handleMouseOut = (event: MouseEvent) => {
      if (event.relatedTarget || event.clientY > 0) return;
      setExitModalOpen(true);
      setExitModalShown(true);
      trackPage('netflix_exit_intent');
    };
    document.addEventListener('mouseout', handleMouseOut);
    return () => document.removeEventListener('mouseout', handleMouseOut);
  }, [exitModalShown]);
  const handleCtaClick = useCallback(() => {
    trackPage('academia_whatsapp_click');
  }, []);
  return <div className="min-h-screen bg-[#141414] font-sans text-white flex flex-col" data-id="element-633" data-mp="3o9mbmzqqj0ee">
      {/* NAV */}
      <header className="sticky top-0 z-50 bg-[#141414]/95 backdrop-blur-sm" data-id="element-634" data-mp="2ylgvq14opi03">
        <nav className="max-w-[1400px] mx-auto px-4 md:px-10 h-14 md:h-16 flex items-center gap-6 md:gap-10" aria-label="Principal" data-id="element-635" data-mp="2ynjvvdzjwrij">
          <a href="/netflix-br" className="flex items-center gap-1.5 md:gap-2 whitespace-nowrap" aria-label="Netflix Bienes Raíces" data-id="element-636" data-mp="38ilnqhl7o7ot">
            <span className="font-netflix text-2xl md:text-4xl leading-none tracking-[0.03em] text-white" data-id="element-637" data-mp="2fufwrd3kdmgj">
              NETFLIX
            </span>
            <span className="font-netflix text-2xl md:text-4xl leading-none tracking-[0.03em] text-[#E50914] ml-1.5 md:ml-2.5" data-id="element-638" data-mp="2au1sm6532wns">
              BIENES RAÍCES
            </span>
          </a>
          <ul className="hidden md:flex items-center gap-6 text-sm text-white/80" data-id="element-639" data-mp="2kt119opumsas">
            {NAV_LINKS.map((link, i) => <li key={link} data-id="element-640" data-mp="1s5ocr904oaeu" data-mpi={i}>
                <a href={CTA_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackPage('academia_whatsapp_click')} className={`hover:text-white transition-colors ${i === 1 ? 'text-white font-semibold' : ''}`} data-id="element-641" data-mp="1s6g76ghzo23j">
                  {link}
                </a>
              </li>)}
          </ul>
          <div className="ml-auto flex items-center gap-4 md:gap-5" data-id="element-642" data-mp="qe3z7cvl8l9p">
            <SearchIcon className="w-5 h-5 text-white/80" aria-hidden="true" data-id="element-643" data-mp="qeww7m4qvwen" />
            <BellIcon className="w-5 h-5 text-white/80" aria-hidden="true" data-id="element-644" data-mp="vbvqc4oes3cu" />
            <div className="w-7 h-7 md:w-8 md:h-8 rounded bg-[#E50914] flex items-center justify-center font-bold text-sm" aria-label="Perfil de Susana" data-id="element-645" data-mp="he74atiluq67">
              S
            </div>
          </div>
        </nav>
      </header>

      <main className="flex-1 w-full max-w-[1400px] mx-auto px-4 md:px-10 pb-16" data-id="element-646" data-mp="3659ntdao0948">
        {/* PLAYER */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative w-full aspect-video overflow-hidden rounded-md bg-black shadow-2xl mt-4 md:mt-6" data-id="element-647" data-mp="3mc4fmsol9e9c">
          <iframe src={YOUTUBE_EMBED_URL} title="Masterclass: Cómo Construir Múltiples Fuentes de Ingreso en Bienes Raíces" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full border-0" data-id="element-648" data-mp="95mb58hsdnxw" />
        </motion.div>

        {/* PRIMARY CTA UNDER THE PLAYER */}
        <motion.a href={CTA_URL} target="_blank" rel="noopener noreferrer" onClick={handleCtaClick} whileHover={{
        scale: 1.03
      }} whileTap={{
        scale: 0.99
      }} animate={{
        scale: [1, 1.025, 1],
        boxShadow: ['0 0 0 0 rgba(229, 9, 20, 0.6)', '0 0 0 16px rgba(229, 9, 20, 0)', '0 0 0 0 rgba(229, 9, 20, 0)']
      }} transition={{
        scale: {
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeInOut'
        },
        boxShadow: {
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeOut'
        }
      }} className="relative mt-5 flex w-full items-center justify-center gap-2 overflow-hidden rounded bg-[#E50914] px-6 py-4 md:py-5 text-base md:text-lg font-bold text-white transition-colors hover:bg-[#C40812] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#141414]" data-id="element-649" data-mp="2jyp1mmqyg0jt">
          <motion.span aria-hidden="true" initial={{
          x: '-120%'
        }} animate={{
          x: '220%'
        }} transition={{
          duration: 1.1,
          repeat: Infinity,
          repeatDelay: 2.2,
          ease: 'easeInOut'
        }} className="pointer-events-none absolute inset-y-0 w-1/3 -skew-x-12 bg-white/25 blur-sm" data-id="element-650" data-mp="110wznk0ot028" />
          <PlayIcon className="relative h-5 w-5 fill-current" aria-hidden="true" data-id="element-651" data-mp="1jy6jv3du6hkj" />
          <span className="relative" data-id="element-652" data-mp="1ssla20zwncif">
            Acceder Ahora a la Academia de Bienes Raíces
          </span>
        </motion.a>

        {/* TITLE BLOCK */}
        <motion.section initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-6 md:mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-8 lg:gap-14" data-id="element-653" data-mp="1orja1hy5ywfe">
          <div data-id="element-654" data-mp="2maht2mm36sd6">
            <div className="flex items-center gap-2 mb-3" data-id="element-655" data-mp="2ma7z6nxx8127">
              <span className="font-netflix text-xl leading-none text-[#E50914]" data-id="element-656" data-mp="27es36dds5v36">
                N
              </span>
              <span className="text-[0.65rem] md:text-xs font-semibold uppercase tracking-[0.3em] text-white/70" data-id="element-657" data-mp="2cghhc1wauovh">
                Masterclass Gratuita
              </span>
            </div>

            <h1 className="font-sans text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase leading-[1.05] mb-4" data-id="element-658" data-mp="23f14lni0iao3">
              Como Construir Multiples Fuentes de Ingreso en Bienes Raíces
            </h1>

            <div className="flex flex-wrap items-center gap-3 text-sm text-white/70 mb-6" data-id="element-659" data-mp="1tlkbklpp8zlh">
              <span className="text-green-400 font-semibold" data-id="element-660" data-mp="1fq8g2c3pz53i">98% para ti</span>
              <span data-id="element-661" data-mp="1jp7klvljgzuf">2026</span>
              <span className="border border-white/40 px-1.5 text-xs leading-5" data-id="element-662" data-mp="1jrjxhucfwk0m">
                +18
              </span>
              <span data-id="element-663" data-mp="1v6exk4kg62k8">1 h 20 min</span>
              <span className="border border-white/40 px-1.5 text-xs leading-5" data-id="element-664" data-mp="1v7ghyzncs1em">
                HD
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-id="element-665" data-mp="1q8vix3ju7nrr">
              <a href={CTA_URL} target="_blank" rel="noopener noreferrer" onClick={handleCtaClick} className="inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#C40812] text-white font-bold text-sm md:text-base py-3 md:py-4 px-6 md:px-8 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#141414]" data-id="element-666" data-mp="1labtuu9y5vv6">
                <PlayIcon className="w-5 h-5 fill-current" aria-hidden="true" data-id="element-667" data-mp="tgrwpzfzkbvp" />
                Acceder Ahora a la Academia de Bienes Raíces
              </a>
              <div className="hidden sm:flex items-center gap-3" data-id="element-668" data-mp="353lnfi3uepf6">
                <span className="w-10 h-10 rounded-full border-2 border-white/40 flex items-center justify-center text-white/70" aria-hidden="true" data-id="element-669" data-mp="39574zgrbnrvp">
                  <PlusIcon className="w-5 h-5" data-id="element-670" data-mp="3j4668e9rcl8o" />
                </span>
                <span className="w-10 h-10 rounded-full border-2 border-white/40 flex items-center justify-center text-white/70" aria-hidden="true" data-id="element-671" data-mp="2lg5wjtihogqf">
                  <ThumbsUpIcon className="w-5 h-5" data-id="element-672" data-mp="2vebsluhm6snd" />
                </span>
              </div>
            </div>
          </div>

          <aside className="text-sm text-white/70 space-y-3 lg:pt-10" data-id="element-673" data-mp="305bule5lc0wq">
            <p data-id="element-674" data-mp="22pxvri29i2de">
              <span className="text-white/50" data-id="element-675" data-mp="22q6735iqkzmh">Con: </span>
              Susana Sánchez G
            </p>
            <p data-id="element-676" data-mp="27nzcvxg4nrm8">
              <span className="text-white/50" data-id="element-677" data-mp="27ng71f3r4tok">Géneros: </span>
              Inversión, Patrimonio, Bienes Raíces
            </p>
            <p data-id="element-678" data-mp="7t2mm4prhlnz">
              <span className="text-white/50" data-id="element-679" data-mp="7tcgi3dxgcyy">Este título es: </span>
              Estratégico, Práctico, Revelador
            </p>
          </aside>
        </motion.section>
      </main>

      {/* FOOTER */}
      <footer className="w-full border-t border-white/10" data-id="element-680" data-mp="mo8r2n97lcf7">
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-8 md:py-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center text-center md:text-left" data-id="element-681" data-mp="mnqq5dblqa0y">
          <div data-id="element-682" data-mp="3q4fway79vhq3">
            <p className="text-lg md:text-2xl font-bold" data-id="element-683" data-mp="3q60s0r5bp6pj">
              Bienes Raíces Academy
            </p>
            <p className="text-sm md:text-lg text-white/70" data-id="element-684" data-mp="3v3ad5uloiw7k">
              Por Susana Sánchez G
            </p>
          </div>

          <div className="text-center leading-tight" data-id="element-685" data-mp="3rhe0vk9ftjl">
            <p className="text-base md:text-xl font-bold tracking-[0.15em] text-white" data-id="element-686" data-mp="32g6fkaox1k5t">
              ACADEMIA
            </p>
            <p className="text-lg md:text-2xl font-extrabold tracking-wide text-gold-metallic" data-id="element-687" data-mp="37cwwt1cgilst">
              BIENES RAÍCES
            </p>
          </div>

          <p className="text-xs text-white/50 md:text-right" data-id="element-688" data-mp="1kh8gs0ldzed7">
            Todos los Derechos Reservados
            <br data-id="element-689" data-mp="1fi5jj2bnecfs" />
            2026
          </p>
        </div>
      </footer>

      <ExitIntentModal open={exitModalOpen} monthlyUrl={MONTHLY_PLAN_URL} annualUrl={ANNUAL_PLAN_URL} onClose={() => setExitModalOpen(false)} onMonthlyClick={() => trackPage('academia_plan_mensual_click')} onAnnualClick={() => trackPage('academia_plan_anual_click')} data-id="element-690" data-mp="1ajkjb2websrc" />
    </div>;
}